import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Table,
  Button,
  Row,
  Select,
  Space,
  Col,
  notification,
  Empty,
  Typography,
  Result,
  Spin,
  Progress,
} from "antd";
import styled from "styled-components";
import { DeleteOutlined, SearchOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { ThemeConfig } from "theme";
import { LoadingSpinnerView } from "components/LoadingSpinner/LoadingSpinner";
import Api from "api";
import { deleteCollection } from "api/helper";
import { initiateSocket } from "helper/socket";
import { useUser } from "helper/UserContext";
import { getTableScroll } from "helper/tableUtils";

interface Props {
  collectionData?: any;
}

/** How often (ms) we poll the backend to check if mapping is done */
const POLL_INTERVAL_MS = 8_000;
/** After this many ms we show a "taking longer than expected" message */
const SLOW_THRESHOLD_MS = 60_000;
/** After this many ms we show a timeout / error state */
const TIMEOUT_MS = 300_000; // 5 minutes

type WaitingStatus = "processing" | "slow" | "timeout" | "error";

const { Option } = Select;
const CollectionColumnMappingTableWrapper = styled.div`
  .ant-table-cell {
    text-align: center;
  }
  .collectionColumnButton .ant-select-selection-item {
    display: none !important;
  }
  .collectionColumnButton .ant-select-selector::before {
    position: absolute;
    content: "Columns";
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: left;
    padding-left: 10px;
    align-items: center;
  }
  .collectionColumnButton .ant-select-selector {
    background: ${ThemeConfig.colors.skyBlue};
    color: #fff;
  }
`;

const CollectionColumnMappingTable = ({ collectionData }: Props) => {
  const [columns, setColumns] = useState<any>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [tableData, setTableData] = useState<any>([]);
  const [originalColumns, setOriginalColumns] = useState<any>([]);
  const [columnsMapping, setColumnsMapping] = useState<any>({});
  const [socketWaiting, setSocketWaiting] = useState<boolean>(false);
  const [waitingStatus, setWaitingStatus] = useState<WaitingStatus>("processing");
  const [elapsedSeconds, setElapsedSeconds] = useState<number>(0);

  const history = useHistory();
  const socketClient = useRef<any>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const elapsedTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTimeRef = useRef<number>(0);
  const { user } = useUser();

  const ResizableTitle = useCallback(
    (props) => {
      const { onResize, width, children, ...restProps } = props;
      const matchingColumn = children.find((s) => s && typeof s === "string");
      return (
        <th {...restProps}>
          <Typography.Paragraph ellipsis>{children}</Typography.Paragraph>
          <Select
            showSearch
            dropdownMatchSelectWidth={false}
            value={columnsMapping && columnsMapping[matchingColumn]}
            onChange={(v) => {
              setColumnsMapping({
                ...columnsMapping,
                [matchingColumn]: v,
              });
            }}
            style={{ width: "100%" }}
          >
            {originalColumns.map((s) => (
              <Option key={s.value} value={s.value}>
                {s.label}
              </Option>
            ))}
          </Select>
        </th>
      );
    },
    [originalColumns, columnsMapping]
  );

  // ── Polling fallback: check if collection is already mapped ──
  // The API returns two different response shapes:
  //   - Unmapped: { data: { is_user_upload, mapped, ... } }
  //   - Mapped:   { id, name, items, column_names, ... }
  // We check for both to detect when processing is complete.
  const pollCollectionStatus = useCallback(async () => {
    try {
      const { data } = await Api.get(`/collections/${collectionData.id}`, {});
      const isMapped =
        data?.data?.mapped === true ||  // unmapped response with mapped flag
        (data?.items !== undefined && !data?.data);  // mapped response has items at top level
      if (isMapped) {
        // Backend finished processing — navigate to the collection
        cleanupTimers();
        setSocketWaiting(false);
        history.push({ pathname: `/collections/${collectionData.id}` });
      }
    } catch (e) {
      console.warn("Polling collection status failed:", e);
    }
  }, [collectionData, history]);

  // ── Timer cleanup helper ──
  const cleanupTimers = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    if (elapsedTimerRef.current) {
      clearInterval(elapsedTimerRef.current);
      elapsedTimerRef.current = null;
    }
  }, []);

  // ── Start waiting: socket + polling + elapsed tracker ──
  const startWaiting = useCallback(() => {
    setSocketWaiting(true);
    setWaitingStatus("processing");
    setElapsedSeconds(0);
    startTimeRef.current = Date.now();

    // Start polling as a fallback for the socket
    pollTimerRef.current = setInterval(() => {
      pollCollectionStatus();
    }, POLL_INTERVAL_MS);

    // Track elapsed time for UI feedback
    elapsedTimerRef.current = setInterval(() => {
      const elapsed = Date.now() - startTimeRef.current;
      setElapsedSeconds(Math.floor(elapsed / 1000));

      if (elapsed >= TIMEOUT_MS) {
        setWaitingStatus("timeout");
      } else if (elapsed >= SLOW_THRESHOLD_MS) {
        setWaitingStatus("slow");
      }
    }, 1000);
  }, [pollCollectionStatus]);

  // ── Cleanup on unmount ──
  useEffect(() => {
    return () => {
      cleanupTimers();
      if (socketClient.current) {
        socketClient.current.disconnect();
      }
    };
  }, [cleanupTimers]);

  // ── Socket listener ──
  useEffect(() => {
    socketClient.current = initiateSocket(user);
    socketClient.current.on("message", (msg) => {
      console.log("socket message is", msg);
      if (
        msg &&
        collectionData &&
        collectionData.id === msg.collection_id &&
        msg.message === "Process Done"
      ) {
        cleanupTimers();
        setSocketWaiting(false);
        history.push({
          pathname: `/collections/${collectionData.id}`,
        });
      }
    });
  }, []);

  // ── Save handler ──
  const onSave = async (values) => {
    if (!Object.keys(columnsMapping).length) {
      notification.error({
        message: "Incomplete mapping!",
        description:
          "Before matching, choose at least one option from a dropdown of any column.",
      });
      return;
    }
    const isAllSkipped = Object.values(columnsMapping).filter(
      (value) => value !== "skipped"
    );
    if (!isAllSkipped.length) {
      notification.error({
        message: "Incomplete mapping!",
        description:
          "Before matching, choose at least one option from a dropdown of any column.",
      });
      return;
    }
    setLoading(true);
    try {
      const { data } = await Api.post(
        `/collections/${collectionData.id}/save_mapped_columns`,
        {
          mapped_columns: columnsMapping,
        }
      );
      setLoading(false);
      startWaiting();
    } catch (e: any) {
      console.log("error", e.response?.data?.messages?.[0]);
      setLoading(false);
      setSocketWaiting(false);
      const errorMsg =
        e?.response?.data?.messages?.[0] || "Failed to map columns";
      notification.error({
        message: "Failed",
        description: errorMsg,
      });
    }
  };

  const handleDeleteCollection = async () => {
    const delRes = await deleteCollection(collectionData.id);
    if (delRes && delRes.error) {
      notification.error({
        message: "Cancel Failed",
        description: delRes.error,
      });
    } else {
      notification.success({
        message: "Cancelled",
        description: "Upload cancelled",
      });
      history.push("/collections/upload_excel_file");
    }
  };

  const handleRetry = async () => {
    cleanupTimers();
    setWaitingStatus("processing");
    startWaiting();
    // Re-trigger the mapping via API
    try {
      await Api.post(
        `/collections/${collectionData.id}/save_mapped_columns`,
        { mapped_columns: columnsMapping }
      );
    } catch (e: any) {
      console.warn("Retry failed:", e);
      setWaitingStatus("error");
    }
  };

  const handleViewCollection = () => {
    cleanupTimers();
    setSocketWaiting(false);
    history.push({ pathname: `/collections/${collectionData.id}` });
  };

  // ── Column initialization from uploaded data ──
  useEffect(() => {
    let topColumns: any = [];
    try {
      topColumns =
        collectionData && collectionData?.attributes?.uploaded_columns;
    } catch (e) {}
    try {
      topColumns = Object.keys(
        collectionData.attributes.document.original_uploaded_data[0]
      );
    } catch (e) {}
    if (!topColumns) {
      topColumns = [];
    }
    if (topColumns) {
      setColumns(
        topColumns.map((s, index) => ({
          title: s,
          dataIndex: s,
          key: s,
          width: 100,
        }))
      );
      const _originalColumns =
        collectionData.attributes.type === "plant"
          ? collectionData.attributes.original_columns
          : collectionData.attributes.original_columns.map((orgCol) => {
              if (orgCol.value === "full_name") {
                return { label: orgCol.label, value: "scientific_names" };
              }
              return orgCol;
            });
      setOriginalColumns(_originalColumns);
      setTableData(collectionData.attributes.document.original_uploaded_data);
      const _columnsMapping = {};
      for (let singleC of topColumns) {
        const matchedColumn = _originalColumns.find(
          (s) => s.label === singleC
        );
        if (matchedColumn) {
          _columnsMapping[singleC] = matchedColumn.value;
        }
      }
      setColumnsMapping(_columnsMapping);
    }
  }, [collectionData]);

  // ── Render: Loading state ──
  if (loading) {
    return <LoadingSpinnerView size={80} />;
  }

  // ── Render: Waiting / processing state ──
  if (socketWaiting) {
    const formatElapsed = (s: number): string => {
      const mins = Math.floor(s / 60);
      const secs = s % 60;
      return mins > 0
        ? `${mins}m ${secs.toString().padStart(2, "0")}s`
        : `${secs}s`;
    };

    if (waitingStatus === "timeout") {
      return (
        <Result
          status="warning"
          title="This is taking longer than expected"
          subTitle={`Processing has been running for ${formatElapsed(elapsedSeconds)}. The server may still be working on it, or something may have gone wrong.`}
          extra={[
            <Button type="primary" key="retry" onClick={handleRetry}>
              Retry Processing
            </Button>,
            <Button key="view" onClick={handleViewCollection}>
              View Collection Anyway
            </Button>,
            <Button
              key="cancel"
              danger
              onClick={handleDeleteCollection}
              icon={<DeleteOutlined />}
            >
              Cancel & Upload New
            </Button>,
          ]}
        />
      );
    }

    return (
      <Result
        icon={<Spin />}
        title="Please wait"
        subTitle={
          waitingStatus === "slow"
            ? `Still processing... (${formatElapsed(elapsedSeconds)}). This is taking a bit longer than usual.`
            : `Matching your species to our database. Elapsed: ${formatElapsed(elapsedSeconds)}`
        }
        extra={[
          <Button type="primary" key="console" onClick={handleViewCollection}>
            Check Collection
          </Button>,
        ]}
      />
    );
  }

  // ── Render: No data state ──
  if (!collectionData || !collectionData.attributes) {
    return <Empty />;
  }

  // ── Render: Column mapping table ──
  return (
    <CollectionColumnMappingTableWrapper>
      <Row style={{ paddingBottom: 10 }} gutter={[8, 8]}>
        <Col lg={17} xs={24}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Step 1 of 2: check that each column is mapped correctly, then match
            your species to our database. You can change any column's mapping
            below, or upload a new list. Step 2 is reviewing the matches.
          </Typography.Title>
        </Col>
        <Col lg={7} xs={24}>
          <Row style={{ justifyContent: "flex-end" }}>
            <Space>
              <Button
                type="primary"
                onClick={onSave}
                icon={<SearchOutlined />}
              >
                Match to our database
              </Button>
              <Button
                type="primary"
                style={{ backgroundColor: "red" }}
                onClick={handleDeleteCollection}
                icon={<DeleteOutlined />}
              >
                Cancel & Upload New
              </Button>
            </Space>
          </Row>
        </Col>
      </Row>
      <Table
        components={{
          header: {
            cell: ResizableTitle,
          },
        }}
        scroll={{
          x: "max-content",
          y: getTableScroll({ extraHeight: null, id: null }),
        }}
        dataSource={tableData}
        pagination={{
          position: ["bottomCenter"],
        }}
        rowKey={(record, i) => record._id || `t${i}`}
        columns={columns}
      />
    </CollectionColumnMappingTableWrapper>
  );
};

export default CollectionColumnMappingTable;
